import { createStore } from 'vuex'
import useHelpers from './helpers'

function includes(value, options) {
  return options.indexOf(value) !== -1
}

function includeIf(state, value, defaultValue = {}) {
  if ( ! state) {
    return defaultValue
  }

  return value
}

function toApplicantResult(type, applicant, necessity = null, income = null) {
  return {
    HakijaTyyppi: type,
    ...includeIf(applicant.nationalityId === 62, {
      Henkilotunnus: applicant.personalNumber,
    }),
    ...includeIf(applicant.nationalityId === 63, {
      Henkilotunnus: applicant.passportNumber,
    }),
    Sukunimi: applicant.lastName,
    Etunimet: applicant.firstNames,
    KansalaisuusId: applicant.nationalityId,
    Henkikirjoituspaikka: applicant.placeOfResidence,
    Yhteystiedot: {
      Puhelin: applicant.phoneNumber,
      Matkapuhelin: applicant.phoneNumber,
      Sahkoposti: applicant.emailAddress,
      Katuosoite: applicant.streetAddress,
      Postinumero: applicant.postalCode,
      Postitoimipaikka: applicant.city,
    },

    // Asunnon tarve
    ...includeIf(necessity, {
      ...includeIf(includes(necessity?.mode, ['homelessness']), {
        AsunnonTarve: {
          AsunnontarveAlkaen: necessity?.startingAt,
          AsunnottomuusAlkaen: necessity?.homelessness?.startingAt,
          EiAsuntoa: true,
          AsunnonTarveId: necessity?.homelessness?.reason,
          MuuSyy: necessity?.otherDetails ?? null
        },
        NykyinenAsunto: {
          Osoite: applicant.streetAddress,
          Postinumero: applicant.postalCode,
          Postitoimipaikka: applicant.city,
        },
      }),
      ...includeIf(includes(necessity?.mode, ['moving']), {
        AsunnonTarve: {
          OnAsunto: true,
          AsunnontarveAlkaen: necessity?.startingAt,
          MuuttovelvoiteId: necessity?.moving?.necessity,
          NykyinenAsuntoSopimatonId: necessity?.moving?.reasonCurrentApartmentIsUnsuitable,
          ...includeIf(necessity?.moving?.reasonCurrentApartmentIsUnsuitable === 190, {
            NykyinenAsuntoSopimatonMuuSyy: necessity?.moving?.otherReasonCurrentApartmentIsUnsuitable
          }),
          MuuSyy: necessity?.otherDetails ?? null
        }
      }),
      ...includeIf(includes(necessity?.mode, ['relocation']), {
        AsunnonTarve: {
          OnAsunto: true,
          AsunnontarveAlkaen: necessity?.startingAt,
          MuuttoPaikkakunnalle: true,
          MuuttoSyyId: necessity?.relocation?.reason,
          ...includeIf(includes(necessity?.relocation?.reason, [78]), {
            UusiOppilaitos: necessity?.relocation?.school?.name
          }, {}),
          ...includeIf(includes(necessity?.relocation?.reason, [77]), {
            UusiTyoAlkaen: necessity?.relocation?.employer?.startingAt,
            UusiTyonantaja: necessity?.relocation?.employer?.name,
            UusiTyopaikkaOsoite: necessity?.relocation?.employer?.address
          }, {}),
          MuuSyy: necessity?.otherDetails ?? null,
        }
      }),
      ...includeIf(includes(necessity?.mode, ['moving', 'relocation']), {
        NykyinenAsunto: {
          AsukkaatKpl: necessity?.currentApartment?.numberOfHabitants,
          Huoneistotyyppi: necessity?.currentApartment?.apartmentType,
          Neliomaara: necessity?.currentApartment?.apartmentSize,
          HallintasuhdeId: necessity?.currentApartment?.ownershipTypeId,
          MuuHallintasuhde: includeIf(
            includes(necessity?.currentApartment.ownershipTypeId, [44]),
            necessity?.currentApartment.otherOwnershipType,
            null
          ),
          Osoite: applicant.streetAddress,
          Postinumero: applicant.postalCode,
          Postitoimipaikka: applicant.city,
          VuokraKk: necessity?.currentApartment?.price,
          ...includeIf(includes(necessity?.currentApartment?.ownershipTypeId, [38, 39]), {
            VuokranantajaNimi: necessity?.currentApartment?.landlord?.name,
            VuokranantajaPuh: necessity?.currentApartment?.landlord?.phoneNumber
          }),
        },
      }, {})
    }),

    // Tulot ja varallisuus
    ...includeIf(income, {
      Varallisuus: {
        VarallisuusOmistaa: income?.wealth?.enabled,
        ...includeIf(income?.wealth.enabled, {
          VarallisuusArvo: income?.wealth?.amount,
          Omistuskohteet: income?.wealth?.ownedPropertyTypeIds,
          KiinteistojenKayttotarkoitukset: income?.wealth?.ownedPropertyUseIds,
          MuuKaytto: income?.wealth?.otherUse,
        }, {
          Omistuskohteet: [336],
        }),
        LainaaOn: income?.debt?.enabled,
        ...includeIf(income?.debt?.enabled, {
          LainaMaara: income?.debt?.amount,
          LainaMaksettuVuosi: income?.debt?.repaidYear,
          LainaMyonnetty: income?.debt?.startingAt
        }),
      },
      Tulot: {
        Bruttotulot: income?.netIncome,
        Elatusmaksu: income?.alimony,
        ...includeIf(income?.debt?.enabled, {
          Opintolaina: income?.debt?.studentLoan,
          MuuLaina: income?.debt?.otherLoan,
        })
      }
    }),
  }
}

const applicant = {
  firstNames: null, // Etunimet*
  lastName: null, // Sukunimi*
  nationalityId: null, // Kansalaisuus* | options.nationality
  personalNumber: null, // Henkilötunnus*
  passportNumber: null, // Passin numero*
  placeOfResidence: null, // Kotipaikkakunta*
  streetAddress: null, // Katuosoite*
  postalCode: null, // Postinumero*
  city: null, // Postitoimipaikka*
  phoneNumber: null, // Matkapuhelin*
  emailAddress: null, // Sähköpostiosoite
}

const otherApplicant = {
  firstNames: null,
  lastName: null,
  nationalityId: null,
  personalNumber: null,
  passportNumber: null,
}

const necessity = {
  startingAt: null, // Asunnon tarve alkaen*
  mode: null, // Minulla ei ole asuntoa, Minulla on asunto, Muutto paikkakunnalle
  homelessness: {
    startingAt: null, // Alkaen
    reason: null, // Syy asunnottomuuteen* | options.reasonsForHomelessness
  },
  moving: {
    startingAt: null, // Muutettava asunnosta pois viimeistään (pp.kk.vvvv)
    necessity: null, // Muuttovelvoite nykyisestä asunnosta* | options.necessitiesForMove
    reasonCurrentApartmentIsUnsuitable: null, // Nykyinen asunto on sopimaton, koska* | options.reasonsCurrentApartmentIsUnsuitable
    otherReasonCurrentApartmentIsUnsuitable: null, // Nykyinen asunto on sopimaton, koska* (muu, mikä?)
  },
  relocation: {
    reason: null, // Syy muuttoon* | options.reasonsForRelocation
    employer: {
      name: null, // Työnantaja*
      address: null, // Työpaikan osoite*
      startingAt: null // Työn alkamispäivä*
    },
    school: {
      name: null, // Oppilaitos*
    }
  },
  currentApartment: {
    numberOfHabitants: null, // Asukkaita kpl*
    apartmentType: null, // Huoneistotyyppi (esim. 1h+kk, 2h+k)*
    apartmentSize: null, // Huoneiston koko m²*
    ownershipTypeId: null, // Hallintasuhde* | options.currentApartmentOwnershipTypes
    otherOwnershipType: null, // Jos muu hallintasuhde, mikä*
    price: null, // Kaikki asumiskustannukset yhteensä €/kk*
    landlord: {
      name: null, // Nykyinen vuokranantaja - Nimi
      phoneNumber: null, // Puhelinnumero
    },
  },
  otherDetails: null
}

const income = {
  netIncome: null, // Bruttotulot €/kk*
  alimony: null, // Elatusmaksu €/kk
  wealth: {
    enabled: null, // Hakijalla on varallisuutta
    ownedPropertyTypeIds: [], // Hakija omistaa kokonaan tai osaksi | options.ownedPropertyTypes
    ownedPropertyUseIds: [], // Kiinteistöjen/asuntojen käyttö | options.ownedPropertyUses
    otherUse: null, // Muu käyttö, mikä
    amount: null, // Kiinteistöjen/asuntojen/osakkeiden nykyinen myyntiarvo, tai hakijan osuus tästä (arvio €)
  },
  debt: {
    enabled: null, // Hakijalla on asunto-, opinto- tai muuta lainaa.
    startingAt: null, // Myöntämispäivä
    amount: null, // Lainan määrä €
    repaidYear: null, // Takaisinmaksuvuosi*
    studentLoan: null, // Opintolaina € (lähetetään tuloihin)
    otherLoan: null, // Muut lainat € (lähetetään tuloihin)
  }
}

function forget() {
  window.localStorage?.removeItem('kas_application_result')
}

function remember(state) {
  window.localStorage?.setItem(
    'kas_application_result', JSON.stringify(state)
  )
}

function init() {
  const remembered = JSON.parse(
    window.localStorage?.getItem('kas_application_result') ?? null
  )

  if (remembered) {
    return remembered
  }

  return {
    favorites: [],
    applicationTypeId: 1,
    locationId: null,
    districtIds: [],
    buildingTypeIds: [],
    apartmentTypeIds: [],
    featureIds: [],
    sizeMin: null,
    sizeMax: null,
    priceMax: null,
    sendEmail: false,
    acceptTerms: false,
    informationHandling: false,
    offerViaSms: true,
    offerViaEmail: false,
    proofOfPregnancy: {
      enabled: false, //  Raskaustodistus
      dueDate: null, // Laskettu aika
    },
    applicants: {
      primary: { ...applicant },
      secondary: {
        enabled: false,
        livingWithPrimary: true,
        ...applicant,
      },
      other: [],
    },
    necessity: {
      primary: { ...necessity },
      secondary: { ...necessity }
    },
    income: {
      primary: { ...income },
      secondary: { ...income }
    },
    additionalInformation: null,
  }
}

export default function useApplicationStore() {
  const { resolvePath, setPath } = useHelpers()

  const store = createStore({
    state: init(),
    getters: {
      resolve: state => name => {
        return resolvePath(state, name)
      },
      isFavorite: state => payload => {
        const id = payload.apartmentId
          ? `A${payload.buildingId}-${payload.apartmentId}`
          : `B${payload.buildingId}`

        const exists = state.favorites.filter(item => item.id === id).length

        return exists !== 0
      },
      tampuuriResult(state) {
        return {
          HakemusTyyppi: state.applicationTypeId,
          ...includeIf(state.proofOfPregnancy.enabled, {
            Raskaustodistus: state.proofOfPregnancy.enabled,
            LaskettuAika: state.proofOfPregnancy.dueDate,
          }),
          EmailVahvistus: state.offerViaEmail,
          TekstiviestiVahvistus: state.offerViaSms,
          YhteystietojenLuovutus: state.informationHandling,
          Kohdistus: {
            NeliorajausMin: state.sizeMin,
            NeliorajausMax: state.sizeMax,
            VuokraMax: state.priceMax,
            ...includeIf(state.additionalInformation, {
              HakuMuuta: state.additionalInformation
            }),
          },
          Erikoisvalinnat: state.featureIds.map(id => {
            return {
              ErikoisvalintaId: id,
            }
          }),
          Huoneistotyyppit: state.apartmentTypeIds.map(id => {
            return {
              HuoneistotyyppiId: id,
            }
          }),
          Kaupungit: includeIf(state.locationId, [
            {
              KaupunkiId: state.locationId,
              Kaupunginosat: state.districtIds.map(id => {
                return {
                  KaupunginosaId: id,
                }
              })
            }
          ], null),
          Kohteet: state.favorites.map(favorite => {
            return { KustannuspaikkaId: favorite.buildingId, HuoneistoId: favorite.apartmentId }
          }),
          Talotyyppit: state.buildingTypeIds.map(id => {
            return {
              TalotyyppiId: id,
            }
          }),
          HakemusOsapuolet: [
            toApplicantResult(1, state.applicants.primary, state.necessity.primary, state.income.primary),
            ...includeIf(state.applicants.secondary.enabled, [
              {
                ...toApplicantResult(2, {
                  ...state.applicants.secondary,
                  ...includeIf(state.applicants.secondary.livingWithPrimary, {
                    streetAddress: state.applicants.primary.streetAddress,
                    postalCode: state.applicants.primary.postalCode,
                    city: state.applicants.primary.city,
                  })
                }, state.necessity.secondary, state.income.secondary),
                AsuuHakijanKanssa: state.applicants.secondary.livingWithPrimary,
              }
            ], []),
            ... state.applicants.other.map(other => {
              return toApplicantResult(3, {
                ...other,
                streetAddress: state.applicants.primary.streetAddress,
                postalCode: state.applicants.primary.postalCode,
                city: state.applicants.primary.city,
              })
            })
          ],
        }
      }
    },
    mutations: {
      forget(state) {
        forget()
        //state = init()
      },
      toggleFavorite(state, payload) {
        const id = payload.apartmentId
          ? `A${payload.buildingId}-${payload.apartmentId}`
          : `B${payload.buildingId}`

        const exists = state.favorites.filter(item => item.id === id).length

        if (exists) {
          state.favorites = state.favorites.filter(item => item.id !== id)
        } else {
          state.favorites = [
            ...state.favorites,
            { id, ...payload }
          ]
        }

        remember(state)
      },
      addOtherApplicant(state) {
        state.applicants.other = [
          ...state.applicants.other,
          { ...otherApplicant },
        ]

        remember(state)
      },
      removeOtherApplicant(state, i) {
        state.applicants.other = state.applicants.other.filter((item, index) => index !== i)

        remember(state)
      },
      set(state, { name, value }) {
        if (name === 'locationId') {
          setPath(state, 'districtIds', [])
        }

        setPath(state, name, value)

        remember(state)
      }
    }
  })

  return {
    store,
  }
}
